import { TextBadge } from '@carvana/ds-badge';
import { NavigableButton } from '@carvana/ds-button';
import React, { memo, useContext } from 'react';
import { FinanceCardType } from '@/common/FinanceCard/constants';
import {
  calculateDaysUntilExpired,
  getFinanceCardType
} from '@/common/FinanceCard/utils';
import TrackWrapper from '@/common/TrackWrapper';
import Sections from '~/constants/home/sections';
import { useIsNative } from '~/context/common/DeviceContext';
import {
  useGetExpirationDate,
  useHasExpiredSoftpull,
  useHasValidSoftPull,
  useIsFinanceLoaded
} from '~/context/home/FinanceContext';
import ModalContext from '~/context/home/ModalContext';
import { useGetUserPurchaseIsLoaded } from '~/context/home/UserPurchaseContext';
import Circle from '~/icons/CountdownCircle';
import { nativeSrpPayload } from '~/native/common/utils';
import { useNativeOpenSoftPullOnClick } from '~/native/home/hooks';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import formatEvent from '~/utilities/formatEvent';
import { CardType, fireAction } from './actionNames';
import DefaultFinanceCard from './DefaultFinanceCard';
import {
  FinanceContentContainer,
  FinanceDetailsContainer,
  FinanceTextGroup,
  FinanceStatusMessage,
  FinanceDetailsText
} from './FinanceCard.styles';
import FinanceLoadingCard from './FinanceLoadingCard';
import {
  OfferCardContainer,
  OfferCardContent,
  HeadingSection,
  OfferHeading
} from './SharedCard.styles';

const maxSoftPullLength = 30;

function Countdown({ daysToExpired }: { daysToExpired: number }) {
  const days = daysToExpired < 0 ? 0 : daysToExpired;

  return (
    <div className="relative flex shrink-0">
      <Circle ratio={days / maxSoftPullLength} />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p
          className="pt-4 text-text-strong t-heading-md"
          data-testid="countdown-days"
        >
          {days.toLocaleString()}
        </p>
        <p
          className="relative -top-4 text-center text-text-strong t-body-xs"
          data-testid="countdown-text"
        >
          day{days === 1 ? '' : 's'}
        </p>
      </div>
    </div>
  );
}

function FinanceCard() {
  const handleNativeOpenSoftPullOnClick = useNativeOpenSoftPullOnClick();
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const hasValidSoftPull = useHasValidSoftPull();
  const hasExpiredSoftPull = useHasExpiredSoftpull();
  const expirationDate = useGetExpirationDate();
  const isNative = useIsNative();

  const status = getFinanceCardType(hasValidSoftPull, hasExpiredSoftPull);
  const daysToExpired = calculateDaysUntilExpired(expirationDate);
  const isDefault = status === FinanceCardType.Default;
  const isExpired = status === FinanceCardType.Expired;

  const ctaText = isDefault
    ? 'Get Pre-Qualified'
    : isExpired
    ? 'Refresh Your Terms'
    : 'Shop Now';

  const ctaHref = isDefault || isExpired ? '/financing' : '/cars';
  const shouldOpenModal = isDefault || isExpired;

  const buttonVariant = isExpired ? 'secondary' : 'primary';

  const commonCustomAttributes = {
    description: 'Finance card',
    daysToExpired,
    status: isDefault ? 'default' : status,
    cta: ctaText
  };

  const handleCtaClick = (
    ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    fireAction(CardType.Finance);

    if (shouldOpenModal) {
      ev.preventDefault();
      handleNativeOpenSoftPullOnClick(ev);
      if (!isNative) {
        setSoftPullModalOpen(true);
      }
    }
  };

  if (isDefault) {
    return <DefaultFinanceCard onCtaClick={handleCtaClick} />;
  }

  return (
    <TrackWrapper
      event={formatEvent('Home feed card', {
        action: 'viewed',
        ...commonCustomAttributes
      })}
      trackOnce
      key={status}
    >
      <OfferCardContainer data-testid="finance-card-pre-qualified">
        <OfferCardContent>
          <HeadingSection>
            <OfferHeading>Your financing</OfferHeading>
            <TextBadge
              variant={isExpired ? 'strong' : 'outline'}
              priority={isExpired ? 'critical' : 'success'}
              data-testid={isExpired ? 'expired-badge' : 'active-badge'}
            >
              {isExpired ? 'Expired' : 'Active terms'}
            </TextBadge>
          </HeadingSection>

          <FinanceContentContainer>
            <Countdown daysToExpired={daysToExpired} />

            <FinanceDetailsContainer>
              <FinanceTextGroup>
                <FinanceStatusMessage data-testid="finance-status-message">
                  {isExpired
                    ? 'Refresh to get instantly pre-qualified.'
                    : "You're pre-qualified!"}
                </FinanceStatusMessage>
                <FinanceDetailsText data-testid="finance-details">
                  {isExpired
                    ? 'No hit to your credit'
                    : 'See real payments on every vehicle.'}
                </FinanceDetailsText>
              </FinanceTextGroup>
            </FinanceDetailsContainer>
          </FinanceContentContainer>

          <NavigableButton
            className="w-full"
            data-analytics-attributes={JSON.stringify({
              ...commonCustomAttributes,
              item: 'cta button'
            })}
            data-analytics-id="Home feed card"
            data-native-included={!shouldOpenModal}
            data-native-payload={shouldOpenModal ? undefined : nativeSrpPayload}
            data-testid="finance-card-cta"
            href={ctaHref}
            onClick={handleCtaClick}
            size="sm"
            variant={buttonVariant}
          >
            {ctaText}
          </NavigableButton>
        </OfferCardContent>
      </OfferCardContainer>
    </TrackWrapper>
  );
}

function FinanceCardWrapper() {
  const isUserPurchaseLoaded = useGetUserPurchaseIsLoaded();
  const isFinanceLoaded = useIsFinanceLoaded();

  return isUserPurchaseLoaded && isFinanceLoaded ? (
    <FinanceCard />
  ) : (
    <FinanceLoadingCard />
  );
}

export default withErrorBoundary(memo(FinanceCardWrapper), {
  section: Sections.FinanceCard
});
